import type { User, AttendanceRecord, OfficeSettings, ShiftSettings, LeaveRequest, OvertimeRequest, SalaryAdvanceRequest } from '../types';

const STORAGE_KEYS = {
  USER: 'Hadir_user',
  TOKEN: 'Hadir_token',
  ATTENDANCE_RECORDS: 'Hadir_attendance',
  OFFICE_SETTINGS: 'Hadir_office',
  SHIFT_SETTINGS: 'Hadir_shift',
  LEAVE_REQUESTS: 'Hadir_leave_requests',
  OVERTIME_REQUESTS: 'Hadir_overtime_requests',
  SALARY_ADVANCE_REQUESTS: 'Hadir_salary_advance_requests',
} as const;

const DEFAULT_OFFICE_SETTINGS: OfficeSettings = {
  name: 'Main Office',
  lat: 40.7128,
  lng: -74.006,
  allowedRadius: 200,
};

const DEFAULT_SHIFT_SETTINGS: ShiftSettings = {
  name: 'Regular Shift',
  startTime: '09:00',
  endTime: '17:00',
};

export const storage = {
  getUser(): User | null {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    return user ? (JSON.parse(user) as User) : null;
  },
  setUser(user: User): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },
  removeUser(): void {
    localStorage.removeItem(STORAGE_KEYS.USER);
  },

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  },
  setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  },
  removeToken(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  },

  getAttendanceRecords(): AttendanceRecord[] {
    const records = localStorage.getItem(STORAGE_KEYS.ATTENDANCE_RECORDS);
    return records ? (JSON.parse(records) as AttendanceRecord[]) : [];
  },
  setAttendanceRecords(records: AttendanceRecord[]): void {
    localStorage.setItem(STORAGE_KEYS.ATTENDANCE_RECORDS, JSON.stringify(records));
  },
  addAttendanceRecord(record: AttendanceRecord): void {
    const records = storage.getAttendanceRecords();
    records.push(record);
    storage.setAttendanceRecords(records);
  },
  updateAttendanceRecord(id: string, updates: Partial<AttendanceRecord>): void {
    const records = storage.getAttendanceRecords();
    const index = records.findIndex((r) => r.id === id);
    if (index !== -1) {
      records[index] = { ...records[index], ...updates };
      storage.setAttendanceRecords(records);
    }
  },

  getOfficeSettings(): OfficeSettings {
    const settings = localStorage.getItem(STORAGE_KEYS.OFFICE_SETTINGS);
    return settings ? (JSON.parse(settings) as OfficeSettings) : DEFAULT_OFFICE_SETTINGS;
  },
  setOfficeSettings(settings: OfficeSettings): void {
    localStorage.setItem(STORAGE_KEYS.OFFICE_SETTINGS, JSON.stringify(settings));
  },

  getShiftSettings(): ShiftSettings {
    const settings = localStorage.getItem(STORAGE_KEYS.SHIFT_SETTINGS);
    return settings ? (JSON.parse(settings) as ShiftSettings) : DEFAULT_SHIFT_SETTINGS;
  },
  setShiftSettings(settings: ShiftSettings): void {
    localStorage.setItem(STORAGE_KEYS.SHIFT_SETTINGS, JSON.stringify(settings));
  },

  getLeaveRequests(): LeaveRequest[] {
    const data = localStorage.getItem(STORAGE_KEYS.LEAVE_REQUESTS);
    return data ? (JSON.parse(data) as LeaveRequest[]) : [];
  },
  setLeaveRequests(requests: LeaveRequest[]): void {
    localStorage.setItem(STORAGE_KEYS.LEAVE_REQUESTS, JSON.stringify(requests));
  },

  getOvertimeRequests(): OvertimeRequest[] {
    const data = localStorage.getItem(STORAGE_KEYS.OVERTIME_REQUESTS);
    return data ? (JSON.parse(data) as OvertimeRequest[]) : [];
  },
  setOvertimeRequests(requests: OvertimeRequest[]): void {
    localStorage.setItem(STORAGE_KEYS.OVERTIME_REQUESTS, JSON.stringify(requests));
  },

  getSalaryAdvanceRequests(): SalaryAdvanceRequest[] {
    const data = localStorage.getItem(STORAGE_KEYS.SALARY_ADVANCE_REQUESTS);
    return data ? (JSON.parse(data) as SalaryAdvanceRequest[]) : [];
  },
  setSalaryAdvanceRequests(requests: SalaryAdvanceRequest[]): void {
    localStorage.setItem(STORAGE_KEYS.SALARY_ADVANCE_REQUESTS, JSON.stringify(requests));
  },

  clearAll(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  },
};
